/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import {normalizeEntity} from '@ion/core/src/entities/EntityUtils';
import {translate} from '@ion/core/src/locale/locale';
import {CRUDPermissions, User} from '@ion/core/src/types/entities';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {MapEditIcon} from '@ion/dls/src/icons/MapEditIcon';
import {NotificationTimeIcon} from '@ion/dls/src/icons/NotificationTimeIcon';
import {SystemDeleteIcon} from '@ion/dls/src/icons/SystemDeleteIcon';
import {ViewRecordIcon} from '@ion/dls/src/icons/ViewRecordIcon';
import {
  auditAdminUser,
  deleteAdminUser,
  editAdminUser
} from './state';
import {useAuthenticatedUser} from './useAuthenticatedUser';

// defines the user row editor prop definitions ...
export interface UserRowEditorTypes {
  user: User;
  includeGlobalSearch?: boolean;
  usersPermissions?: CRUDPermissions;
}

// defines the styles used by this component ...
const useStyles = makeStyles(theme => ({
  root : {
    display   : 'block',
    '&:focus' : {
      outline : 'none'
    }
  }
}));

/**
 * Defines the UserRowEditor component. This component contains the cell actions
 * for admin users grid.
 *
 * ```typescript
 *
 *   import {UserRowEditor} from './UserRowEditor';
 *
 *   <UserRowEditor .../>
 *
 * ```
 */
export const UserRowEditor = ({
  user,
  usersPermissions,
  includeGlobalSearch = false
}: UserRowEditorTypes) => {
  const classes = useStyles();
  const currentUser = useAuthenticatedUser();
  const dispatch    = useDispatch();
  const cellMenuRef = useRef(null);
  const {
    hasReadPermission,
    hasUpdatePermission,
    hasDeletePermission
  } = usersPermissions || {};

  function closeMenu() {
    cellMenuRef.current.closeCellMenu(cellMenuRef.current, true);
  };

  function editUser() {
    dispatch(editAdminUser(normalizeEntity(user, true), true, false));
    closeMenu();
  };
  function viewUser() {
    dispatch(editAdminUser(normalizeEntity(user, true), true, true));
    closeMenu();
  };

  function auditUser() {
    dispatch(auditAdminUser(user));
    closeMenu();
  };

  function deleteUser() {
    dispatch(deleteAdminUser(user));
    closeMenu();
  };

  return (
    <>
      <CellMenu ref={cellMenuRef} menuItems={
        <div className={classes.root}>
          <CellMenuItem icon={<ViewRecordIcon />}
            onClick={viewUser}
            disabled={!hasReadPermission}
            label={translate('admin-users:grid.menu.viewUser')} />
          <CellMenuItem icon={<MapEditIcon />}
            onClick={editUser}
            disabled={!hasUpdatePermission}
            label={translate('admin-users:grid.menu.editUser')} />
          <CellMenuItem icon={<NotificationTimeIcon />}
            onClick={auditUser}
            label={translate('admin-users:grid.menu.auditUser')} />
          {!includeGlobalSearch && (
            <CellMenuItem icon={<SystemDeleteIcon />}
              disabled={currentUser?.id === user?.id || !hasDeletePermission}
              onClick={deleteUser}
              label={translate('admin-users:grid.menu.deleteUser')} />
          )}
        </div>
      } />
    </>
  );
};
